import React, { useState, useEffect } from 'react';
import { downloadExcelFile, fetchQuizAnalytics } from '../../../api/quizeApi';
import { Download, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';

const QuizAnalyticsModal = ({ isOpen, onClose, quizId = 13 }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const loadAnalytics = async () => {
        try {
          setLoading(true);
          const data = await fetchQuizAnalytics(quizId, currentPage, limit);
          setAnalyticsData(data);
          setError(null);
        } catch (err) {
          setError("Failed to load quiz analytics. Please try again later.");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      loadAnalytics();
    }
  }, [isOpen, quizId, currentPage, limit]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Calculate percentage score
  const calculatePercentage = (mark, totalMark) => {
    return totalMark > 0 ? Math.round((mark / totalMark) * 100) : 0;
  };

  // Format date to be more readable
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleDownload = async (quizId) => {
    setDownloading(true);
    setError(null);

    try {
      await downloadExcelFile(quizId);
    } catch (err) {
      setError('Failed to download Excel file.');
    } finally {
      setDownloading(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-600';
    if (percentage >= 60) return 'bg-blue-600';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center">
            {analyticsData?.data?.length > 0 
              ? `Quiz Analytics: ${analyticsData.data[0].title}` 
              : 'Quiz Analytics'}
          </h2>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200 focus:outline-none transition-transform hover:scale-110"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-500 mx-auto" />
                <p className="mt-4 text-gray-600">Loading quiz analytics...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-red-500 p-4 border border-red-300 rounded-md bg-red-50 shadow-sm">
              {error}
            </div>
          ) : !analyticsData || !analyticsData.data || analyticsData.data.length === 0 ? (
            <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-lg">No quiz attempts found.</p>
              <p className="mt-2 text-sm">Students haven't attempted this quiz yet.</p>
            </div>
          ) : (
            <>
              {/* Download Button */}
              <div className="mb-4 flex justify-end">
                <button 
                  onClick={() => handleDownload(quizId)}
                  disabled={downloading}
                  className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md shadow-sm transition duration-150 disabled:bg-blue-300"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download size={18} className="mr-2" />
                      Download Excel Report
                    </>
                  )}
                </button>
              </div>

              {/* Leaderboard Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700">
                      <th className="p-3 text-left font-semibold">Rank</th>
                      <th className="p-3 text-left font-semibold">Name</th>
                      <th className="p-3 text-left font-semibold">Admission</th>
                      <th className="p-3 text-left font-semibold">Score</th>
                      <th className="p-3 text-left font-semibold">Percentage</th>
                      <th className="p-3 text-left font-semibold hidden sm:table-cell">Attempt Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.data.map((attempt, index) => {
                      const percentage = calculatePercentage(attempt.mark, attempt.totalMark);
                      const scoreColor = getScoreColor(percentage);
                      
                      return (
                        <tr 
                          key={attempt.attempt_id} 
                          className={`border-t hover:bg-gray-50 transition-colors`}
                        >
                          <td className="p-3 text-center md:text-left">
                            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-800 font-medium text-sm">
                              {(currentPage - 1) * limit + index + 1}
                            </span>
                          </td>
                          <td className="p-3 font-medium">
                            {attempt.first_name} {attempt.last_name}
                          </td>
                          <td className="p-3 text-gray-600">{attempt.admission}</td>
                          <td className="p-3 font-medium text-gray-900">
                            {attempt.mark} / {attempt.totalMark}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center">
                              <div className="w-full max-w-24 bg-gray-200 rounded-full h-2 mr-2">
                                <div 
                                  className={`${scoreColor} h-2 rounded-full`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="font-medium whitespace-nowrap">{percentage}%</span>
                            </div>
                          </td>
                          <td className="p-3 text-gray-600 hidden sm:table-cell">{formatDate(attempt.attempt_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer with Pagination */}
        {analyticsData?.pagination && (
          <div className="bg-gray-50 px-4 py-4 sm:px-6 border-t">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-500 order-2 sm:order-1">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, analyticsData.pagination.totalItems)} of {analyticsData.pagination.totalItems} entries
              </div>
              
              <div className="flex space-x-1 order-1 sm:order-2">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!analyticsData.pagination.hasPrevPage}
                  className={`flex items-center px-3 py-1 rounded ${!analyticsData.pagination.hasPrevPage ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </button>
                
                <div className="hidden md:flex space-x-1">
                  {Array.from({ length: analyticsData.pagination.totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first page, last page, current page, and pages around current page
                      return page === 1 || 
                             page === analyticsData.pagination.totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      // Add ellipsis when there are gaps in the page numbers
                      const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsisBefore && (
                            <span className="px-3 py-1 text-gray-500">...</span>
                          )}
                          <button 
                            onClick={() => handlePageChange(page)}
                            className={`min-w-8 px-3 py-1 rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>
                
                <div className="inline-flex items-center md:hidden">
                  <span className="px-3 py-1 bg-white border border-gray-300 rounded">
                    {currentPage} / {analyticsData.pagination.totalPages}
                  </span>
                </div>
                
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!analyticsData.pagination.hasNextPage}
                  className={`flex items-center px-3 py-1 rounded ${!analyticsData.pagination.hasNextPage ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  aria-label="Next page"
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizAnalyticsModal;